import React from 'react';
import { Input, InputNumber, Select, Card, Row, Col, Typography, Checkbox } from 'antd';
import { FJ130 } from '../../../services/j203Api';
import { fmt, invoiceTypeLabel } from '../helpers';
import LookupField from '../../../components/LookupField';

const { TextArea } = Input;
const { Text } = Typography;

interface LookupNames {
  customer: string;
  employee: string;
  department: string;
  currency: string;
  [key: string]: string;
}

interface Tab1Props {
  formData: Partial<FJ130>;
  names: LookupNames;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FJ130) => FJ130[keyof FJ130];
  setField: (key: keyof FJ130, value: FJ130[keyof FJ130]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = ({
  formData, names, isEditing, isFieldDisabled, getField, setField, resolveSingleName,
}) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Left */}
      <div>
        <Card size="small" title="基本資料" style={{ marginBottom: 8 }}>
          <Row gutter={[12, 8]}>
            <Col span={6}><Text type="secondary">申請單號</Text></Col>
            <Col span={18}>
              <LookupField
                lookupType="sampleApplication"
                value={(getField('fj130_10') as string) || ''}
                onChange={(code) => setField('fj130_10', code)}
                disabled={isFieldDisabled('fj130_10')}
                style={{ maxWidth: 200 }}
                title="搜尋樣品申請單"
              />
            </Col>

            <Col span={6}><Text type="secondary">輸單人員</Text></Col>
            <Col span={18}>
              <Input size="small" disabled value={getField('fj130_91') as string} style={{ maxWidth: 140 }} />
            </Col>

            <Col span={6}><Text type="secondary">業務代號</Text></Col>
            <Col span={18}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input size="small" disabled={isFieldDisabled('fj130_21')}
                  value={getField('fj130_21') as string}
                  onChange={e => setField('fj130_21', e.target.value)}
                  onBlur={() => resolveSingleName('employee', 'employee', getField('fj130_21') as string)}
                  style={{ maxWidth: 140 }}
                />
                <Text type="secondary">{names.employee}</Text>
              </div>
            </Col>

            <Col span={6}><Text type="secondary">客戶代號</Text></Col>
            <Col span={18}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input size="small" disabled={isFieldDisabled('fj130_12')}
                  value={getField('fj130_12') as string}
                  onChange={e => setField('fj130_12', e.target.value)}
                  onBlur={() => resolveSingleName('customer', 'customer', getField('fj130_12') as string)}
                  style={{ maxWidth: 140 }}
                />
                <Text type="secondary">{names.customer}</Text>
              </div>
            </Col>

            <Col span={6}><Text type="secondary">幣別</Text></Col>
            <Col span={18}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input size="small" disabled={isFieldDisabled('fj130_14')}
                  value={getField('fj130_14') as string}
                  onChange={e => setField('fj130_14', e.target.value)}
                  onBlur={() => resolveSingleName('currency', 'currency', getField('fj130_14') as string)}
                  style={{ maxWidth: 100 }}
                />
                <Text type="secondary">{names.currency}</Text>
              </div>
            </Col>

            <Col span={6}><Text type="secondary">匯率</Text></Col>
            <Col span={18}>
              <InputNumber size="small" disabled={isFieldDisabled('fj130_15')}
                value={formData.fj130_15 || 0}
                onChange={v => setField('fj130_15', v || 0)}
                style={{ maxWidth: 140 }}
              />
            </Col>
          </Row>
        </Card>

        <Card size="small" title="備註" style={{ marginBottom: 8 }}>
          <TextArea rows={3} disabled={isFieldDisabled('fj130_31')}
            value={getField('fj130_31') as string}
            onChange={e => setField('fj130_31', e.target.value)}
          />
        </Card>
      </div>

      {/* Right */}
      <div>
        <Card size="small" title="交運資訊" style={{ marginBottom: 8 }}>
          <Row gutter={[12, 8]}>
            <Col span={8}><Text type="secondary">發票種類</Text></Col>
            <Col span={16}>
              <Select size="small" disabled={isFieldDisabled('fj130_171')}
                value={formData.fj130_171}
                onChange={v => setField('fj130_171', v)}
                style={{ width: 140 }}
                options={[
                  { value: 1, label: invoiceTypeLabel(1) },
                  { value: 2, label: invoiceTypeLabel(2) },
                  { value: 3, label: invoiceTypeLabel(3) },
                  { value: 4, label: 'DN-內' },
                  { value: 5, label: 'DN-外' },
                ]}
              />
            </Col>

            <Col span={8}><Text type="secondary">發票種類</Text></Col>
            <Col span={16}>
              <Checkbox disabled checked={!!formData.fj130_16}>{formData.fj130_16 ? '是' : '否'}</Checkbox>
            </Col>

            <Col span={8}><Text type="secondary">收費</Text></Col>
            <Col span={16}>
              <Checkbox disabled checked={!!formData.fj130_23}>{formData.fj130_23 ? '是' : '否'}</Checkbox>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="金額">
          <Row gutter={[12, 4]}>
            <Col span={8}><Text type="secondary">未稅交運金額</Text></Col>
            <Col span={16}><Text strong>{fmt(formData.fj130_18 as number)}</Text></Col>
            <Col span={8}><Text type="secondary">稅額</Text></Col>
            <Col span={16}><Text>{fmt(formData.fj130_19 as number)}</Text></Col>
            <Col span={8}><Text type="secondary">含稅交運金額</Text></Col>
            <Col span={16}><Text strong>{fmt(formData.fj130_181 as number)}</Text></Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default React.memo(Tab1BasicInfo);
